import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Building2, ChevronsUpDown, Check, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

export default function TenantSwitcher() {
  const { user, company, checkAuth } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switchingId, setSwitchingId] = useState("");

  const isAgencyAdmin = user?.role === "agency_admin" || user?.role === "platform_admin";

  const load = useCallback(async () => {
    if (!isAgencyAdmin) return;
    try {
      setLoading(true);
      const r = await api.get("/agency/clients");
      setClients(r.data.clients || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [isAgencyAdmin]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!isAgencyAdmin) return null;

  const onSwitch = async (cid) => {
    if (cid === company?.company_id) { setOpen(false); return; }
    setSwitchingId(cid);
    try {
      await api.post("/agency/switch-client", { company_id: cid });
      await checkAuth();
      toast.success("Mandant gewechselt");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wechsel fehlgeschlagen");
    } finally {
      setSwitchingId("");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="tenant-switcher-trigger"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition text-left"
        >
          <Building2 className="w-4 h-4 text-orange-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 leading-none mb-0.5">Mandant</p>
            <p className="text-sm font-semibold text-slate-900 truncate leading-tight">{company?.name || "Kein Mandant"}</p>
          </div>
          <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start" data-testid="tenant-switcher-popover">
        <div className="px-3 py-2 border-b border-slate-100">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Mandant wechseln</p>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-4 text-sm text-slate-500">Lade …</div>
          ) : clients.length === 0 ? (
            <div className="px-3 py-4 text-sm text-slate-500">Keine Mandanten.</div>
          ) : clients.map(c => {
            const active = c.company_id === company?.company_id;
            return (
              <button
                key={c.company_id}
                data-testid={`tenant-switch-item-${c.company_id}`}
                onClick={() => onSwitch(c.company_id)}
                disabled={switchingId === c.company_id}
                className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 ${active ? "bg-orange-50/40" : ""}`}
              >
                <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 truncate">{c.company_name}</p>
                  <p className="text-[11px] text-slate-500 truncate">{c.leads_this_month} Leads · {c.plan_name}</p>
                </div>
                {active && <Check className="w-4 h-4 text-orange-600 shrink-0" />}
              </button>
            );
          })}
        </div>
        <div className="border-t border-slate-100">
          <button
            data-testid="tenant-switcher-manage"
            onClick={() => { setOpen(false); nav("/agency/clients"); }}
            className="w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Mandanten verwalten
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
